/**
 * Shared helpers for event-draft optimistic locking.
 * Server draft `version` is the single source of truth between wizard steps.
 */

export class DraftVersionConflictError extends Error {
  readonly code = 'DRAFT_VERSION_CONFLICT';
  readonly latestVersion: number | null;

  constructor(message: string, latestVersion: number | null = null) {
    super(message);
    this.name = 'DraftVersionConflictError';
    this.latestVersion = latestVersion;
  }
}

export function isDraftVersionConflict(err: unknown): boolean {
  if (err instanceof DraftVersionConflictError) return true;
  const response = (err as { response?: { data?: { message?: string; errors?: Record<string, unknown> } } })
    ?.response;
  const message = response?.data?.message ?? '';
  const versionErrors = response?.data?.errors?.version;
  const versionMessages = Array.isArray(versionErrors) ? versionErrors : [versionErrors];

  return [message, ...versionMessages]
    .filter((value): value is string => typeof value === 'string')
    .some((value) => value.toLowerCase().includes('draft has been modified by another session'));
}

function parseVersionCandidate(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 1) {
    return Math.trunc(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 1) return Math.trunc(parsed);
  }
  return null;
}

/**
 * Reads draft version from API envelopes:
 * `{ version }` or `{ data: { version } }` (create/save/load responses).
 */
export function extractDraftVersion(data: unknown): number | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>).version
      : undefined;
  return parseVersionCandidate(record.version) ?? parseVersionCandidate(nested);
}

/** Accept incoming server versions; never apply an older value unless forced (conflict reload). */
export function shouldAcceptDraftVersion(
  current: number | null,
  incoming: number | null,
  options?: { allowDowngrade?: boolean },
): incoming is number {
  if (incoming === null || !Number.isFinite(incoming) || incoming < 1) return false;
  if (current === null || options?.allowDowngrade) return true;
  return incoming >= current;
}

export type DraftVersionLogEvent = {
  eventId?: string | null;
  step?: string;
  submittedVersion?: number | null;
  returnedVersion?: number | null;
  phase: 'start' | 'complete' | 'conflict' | 'conflict-retry' | 'skip' | 'stale-ignored';
  inFlight?: boolean;
};

export function logDraftVersion(event: DraftVersionLogEvent): void {
  if (process.env.NODE_ENV === 'production') return;
   
  console.debug('[event-draft-version]', {
    eventId: event.eventId ?? null,
    step: event.step ?? null,
    submittedVersion: event.submittedVersion ?? null,
    returnedVersion: event.returnedVersion ?? null,
    phase: event.phase,
    inFlight: event.inFlight ?? false,
  });
}

/**
 * Serializes draft saves so request B waits for A and can use A's returned version.
 */
export class DraftSaveQueue {
  private chain: Promise<unknown> = Promise.resolve();
  private active = 0;

  get isInFlight(): boolean {
    return this.active > 0;
  }

  enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.chain.then(async () => {
      this.active += 1;
      try {
        return await fn();
      } finally {
        this.active -= 1;
      }
    });
    this.chain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }
}

export type DraftSaveAttemptResult = {
  version: number;
  response: unknown;
};

/**
 * Runs a versioned draft save. The caller's payload is closed over and reused
 * on conflict retry — only the version token is refreshed from the server.
 */
export async function saveDraftWithConflictRetry(options: {
  submittedVersion: number;
  save: (version: number) => Promise<unknown>;
  resolveLatestVersion: () => Promise<number | null>;
  maxRetries?: number;
  onConflict?: (latestVersion: number | null, attempt: number) => void;
}): Promise<DraftSaveAttemptResult> {
  const maxRetries = options.maxRetries ?? 1;
  let version = options.submittedVersion;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const response = await options.save(version);
      const returned = extractDraftVersion(response);
      if (returned === null) {
        throw new Error('Invalid draft version returned by backend.');
      }
      return { version: returned, response };
    } catch (err) {
      if (!isDraftVersionConflict(err) || attempt >= maxRetries) {
        throw err;
      }
      const latest = await options.resolveLatestVersion();
      options.onConflict?.(latest, attempt + 1);
      if (latest === null) {
        throw err;
      }
      // Retry the SAME save payload with the corrected version token.
      version = latest;
    }
  }

  throw new Error('Draft save conflict retry exhausted.');
}
